import { ScrollView, View, Text, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useColors, ThemeColors } from '../hooks/useColors';

export default function PrivacyPolicyScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.title}>JadeTrade Privacy Policy</Text>
        <Text style={styles.meta}>Effective Date: December 2025</Text>

        <Text style={styles.section}>1. Introduction</Text>
        <Text style={styles.paragraph}>
          JadeTrade Marketplace LLC ("JadeTrade," "we," "us," or "our") is committed to protecting your 
          privacy. This Privacy Policy describes how we collect, use, disclose, and protect your personal 
          information when you use our mobile application, website, and related services (collectively, the 
          "Services").
        </Text>
        <Text style={styles.paragraph}>
          Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge 
          that you have read, understood, and agreed to our practices. If you do not agree, please 
          discontinue use of our Services.
        </Text>

        <Text style={styles.section}>2. Information We Collect</Text>
        <Text style={styles.subSection}>2.1 Information You Provide to Us</Text>
        <Text style={styles.paragraph}>
          We collect personal information you voluntarily provide, including when you:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Register or update your account (e.g., name, email, mobile number, password)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Complete your profile (e.g., addresses, payment methods)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Communicate via in-app messaging
          </Text>
          <Text style={styles.listItem}>
            {"•"} Place or fulfill orders (e.g., product selections, delivery notes)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Submit identification for age-restricted purchases
          </Text>
          <Text style={styles.listItem}>
            {"•"} Submit feedback, ratings, or customer service requests
          </Text>
        </View>
        <Text style={styles.paragraph}>
          If you are a vendor, we also collect:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Business name and operating address
          </Text>
          <Text style={styles.listItem}>
            {"•"} Business category and descriptions
          </Text>
          <Text style={styles.listItem}>
            {"•"} Product and inventory data
          </Text>
          <Text style={styles.listItem}>
            {"•"} Bank account details for payouts (processed securely via Stripe Connect)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Information required by Stripe Connect for identity verification (e.g., government ID, tax ID, date of birth)
          </Text>
        </View>

        <Text style={styles.subSection}>2.2 Information Collected Automatically</Text>
        <Text style={styles.paragraph}>
          When you use our Services, we may automatically collect:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Device Info: Type, operating system, device ID, app version
          </Text>
          <Text style={styles.listItem}>
            {"•"} Location Data: Precise location (when enabled)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Usage Data: Browsing actions, features used, search queries, screen views, tap interactions
          </Text>
          <Text style={styles.listItem}>
            {"•"} Session Recordings: Visual recordings of app interactions including navigation, scrolls, and taps, used to improve user experience and app performance
          </Text>
          <Text style={styles.listItem}>
            {"•"} Transaction Data: Purchase history and delivery data
          </Text>
          <Text style={styles.listItem}>
            {"•"} Log Data: IP address, access timestamps, and system error reports
          </Text>
          <Text style={styles.listItem}>
            {"•"} Crash and Error Data: Application crashes, error logs, and diagnostic information
          </Text>
          <Text style={styles.listItem}>
            {"•"} Push Notification Tokens: Device identifiers used to deliver push notifications
          </Text>
        </View>
        <Text style={styles.paragraph}>
          Session recordings do not capture sensitive information such as passwords, payment card numbers, or authentication credentials.
        </Text>

        <Text style={styles.subSection}>2.3 Information From Third Parties</Text>
        <Text style={styles.paragraph}>
          We may receive additional data from:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Uber Direct: Delivery status, driver location, and delivery completion information
          </Text>
          <Text style={styles.listItem}>
            {"•"} Stripe Connect: Payment confirmations, payout status, and identity verification status
          </Text>
          <Text style={styles.listItem}>
            {"•"} Supabase: Authentication and session activity
          </Text>
          <Text style={styles.listItem}>
            {"•"} Google Maps API: Geolocation and address validation
          </Text>
          <Text style={styles.listItem}>
            {"•"} Google Sign-In: Name, email address, profile photo, and authentication credentials (when you choose to sign in with Google)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Apple Sign-In: Name and email address (if shared) and authentication credentials (when you choose to sign in with Apple)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Twilio: Phone number verification and messaging status
          </Text>
          <Text style={styles.listItem}>
            {"•"} PostHog: Aggregated analytics and usage data
          </Text>
        </View>

        <Text style={styles.section}>3. How We Use Your Information</Text>
        <Text style={styles.paragraph}>We use your data to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Create and maintain your account
          </Text>
          <Text style={styles.listItem}>
            {"•"} Process, fulfill, and deliver orders
          </Text>
          <Text style={styles.listItem}>
            {"•"} Enable vendor-buyer messaging
          </Text>
          <Text style={styles.listItem}>
            {"•"} Coordinate deliveries via Uber Direct
          </Text>
          <Text style={styles.listItem}>
            {"•"} Process payments via Stripe
          </Text>
          <Text style={styles.listItem}>
            {"•"} Facilitate seller onboarding, identity verification, and payouts via Stripe Connect
          </Text>
          <Text style={styles.listItem}>
            {"•"} Provide customer support and resolve issues
          </Text>
          <Text style={styles.listItem}>
            {"•"} Send confirmations, updates, and promotional content
          </Text>
          <Text style={styles.listItem}>
            {"•"} Send promotional offers, incentives, and discount offers
          </Text>
          <Text style={styles.listItem}>
            {"•"} Improve app performance and user experience
          </Text>
          <Text style={styles.listItem}>
            {"•"} Verify identity for legal or compliance purposes
          </Text>
          <Text style={styles.listItem}>
            {"•"} Prevent fraud and ensure platform security
          </Text>
          <Text style={styles.listItem}>
            {"•"} Comply with applicable legal obligations
          </Text>
        </View>

        <Text style={styles.section}>4. How We Share Your Information</Text>
        <Text style={styles.subSection}>4.1 With Vendors</Text>
        <Text style={styles.paragraph}>When you make a purchase, we share:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Your name and order details
          </Text>
          <Text style={styles.listItem}>
            {"•"} Delivery address (if applicable)
          </Text>
          <Text style={styles.listItem}>
            {"•"} The seller's designated pickup location (shared with you as part of order tracking and fulfillment)
          </Text>
          <Text style={styles.listItem}>
            {"•"} In-app communications
          </Text>
        </View>

        <Text style={styles.subSection}>4.2 With Delivery Providers (Uber Direct)</Text>
        <Text style={styles.paragraph}>For delivery, we share:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Pickup and drop-off addresses (including the buyer's delivery address and the seller's pickup location)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Buyer and vendor contact info
          </Text>
          <Text style={styles.listItem}>
            {"•"} Delivery instructions and order contents
          </Text>
        </View>

        <Text style={styles.subSection}>4.3 With Payment Processors</Text>
        <Text style={styles.paragraph}>To complete transactions, we share:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Purchase amount and items
          </Text>
          <Text style={styles.listItem}>
            {"•"} Payment method and billing details
          </Text>
          <Text style={styles.listItem}>
            {"•"} Seller identity and payout-related information (via Stripe Connect)
          </Text>
        </View>

        <Text style={styles.subSection}>4.4 With Service Providers</Text>
        <Text style={styles.paragraph}>We work with trusted vendors to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Host infrastructure
          </Text>
          <Text style={styles.listItem}>
            {"•"} Process payments
          </Text>
          <Text style={styles.listItem}>
            {"•"} Provide maps/geolocation
          </Text>
          <Text style={styles.listItem}>
            {"•"} Send notifications
          </Text>
          <Text style={styles.listItem}>
            {"•"} Analyze usage and improve performance
          </Text>
        </View>
        <Text style={styles.paragraph}>
          These providers access your data solely to perform services on our behalf and are contractually 
          bound to protect it.
        </Text>

        <Text style={styles.subSection}>4.5 Legal Requirements</Text>
        <Text style={styles.paragraph}>We may disclose information to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Comply with legal obligations or court orders
          </Text>
          <Text style={styles.listItem}>
            {"•"} Respond to government requests
          </Text>
          <Text style={styles.listItem}>
            {"•"} Enforce our Terms and safeguard rights, safety, or property
          </Text>
          <Text style={styles.listItem}>
            {"•"} Prevent fraud or technical issues
          </Text>
          <Text style={styles.listItem}>
            {"•"} Facilitate business transfers such as mergers or acquisitions
          </Text>
        </View>

        <Text style={styles.section}>5. Data Storage & Security</Text>
        <Text style={styles.subSection}>5.1 Storage</Text>
        <Text style={styles.paragraph}>
          Data is stored on encrypted, secure servers. Authentication uses industry-standard token-based 
          mechanisms.
        </Text>

        <Text style={styles.subSection}>5.2 Security Measures</Text>
        <Text style={styles.paragraph}>We use technical and organizational safeguards including:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} SSL encryption (in transit and at rest)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Access controls and permissions
          </Text>
          <Text style={styles.listItem}>
            {"•"} Regular vulnerability scanning and audits
          </Text>
          <Text style={styles.listItem}>
            {"•"} Secure coding practices
          </Text>
          <Text style={styles.listItem}>
            {"•"} Employee training on data handling
          </Text>
        </View>

        <Text style={styles.subSection}>5.3 Retention</Text>
        <Text style={styles.paragraph}>
          We retain personal data only as long as necessary for business, legal, or operational purposes. 
          Afterward, it is securely deleted or anonymized.
        </Text>

        <Text style={styles.section}>6. Your Rights & Choices</Text>
        <Text style={styles.paragraph}>
          Depending on your jurisdiction, you may have rights regarding your personal information.
        </Text>

        <Text style={styles.subSection}>6.1 Account Management</Text>
        <Text style={styles.paragraph}>
          You may update or correct your profile within the app.
        </Text>

        <Text style={styles.subSection}>6.2 Location Settings</Text>
        <Text style={styles.paragraph}>
          You can enable or disable location sharing through your device's system preferences.
        </Text>

        <Text style={styles.subSection}>6.3 Notifications</Text>
        <Text style={styles.paragraph}>
          Manage push and email notifications within your app settings.
        </Text>

        <Text style={styles.subSection}>6.4 Account Deletion</Text>
        <Text style={styles.paragraph}>
          You can request account deletion in-app or by contacting us. Some information may be retained 
          for legal or operational reasons.
        </Text>

        <Text style={styles.subSection}>6.5 Privacy Rights (California, EU/EEA, etc.)</Text>
        <Text style={styles.paragraph}>Subject to applicable law, you may have rights to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Access your personal information
          </Text>
          <Text style={styles.listItem}>
            {"•"} Request correction or deletion
          </Text>
          <Text style={styles.listItem}>
            {"•"} Restrict or object to data processing
          </Text>
          <Text style={styles.listItem}>
            {"•"} Receive a copy of your data (portability)
          </Text>
        </View>
        <Text style={styles.paragraph}>
          To exercise these rights, contact: privacy@jadetrademarketplace.com
        </Text>

        <Text style={styles.section}>7. Tracking Technologies</Text>
        <Text style={styles.paragraph}>
          We use tracking and analytics tools to understand how users interact with our Services and to improve functionality and user experience, including:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Device identifiers and advertising IDs
          </Text>
          <Text style={styles.listItem}>
            {"•"} In-app activity tracking (such as session duration, screen views, and interaction rates)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Session replay technology that records user interactions such as navigation, taps, and scrolling to identify usability issues
          </Text>
          <Text style={styles.listItem}>
            {"•"} Event tracking and user journey analysis
          </Text>
          <Text style={styles.listItem}>
            {"•"} Google Maps SDK for location-based services
          </Text>
          <Text style={styles.listItem}>
            {"•"} Error and crash monitoring to diagnose and resolve technical issues
          </Text>
        </View>
        <Text style={styles.paragraph}>
          You may limit or disable certain tracking features through your device settings. Session replay recordings do not capture sensitive information such as passwords or payment card numbers.
        </Text>

        <Text style={styles.section}>8. Third-Party Services</Text>
        <Text style={styles.subSection}>8.1 Uber Direct</Text>
        <Text style={styles.paragraph}>
          Used for fulfillment of deliveries. Pickup and delivery addresses, contact information, and order details are shared to facilitate delivery. Their privacy practices are governed by Uber's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.2 Stripe</Text>
        <Text style={styles.paragraph}>
          Used for secure payment processing, seller payouts, and onboarding. Stripe Connect also handles seller identity verification. Data is shared only as necessary and governed by Stripe's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.3 Google Maps API</Text>
        <Text style={styles.paragraph}>
          Used to provide address validation, delivery distance calculations, and location-based services. Subject to Google's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.4 Supabase</Text>
        <Text style={styles.paragraph}>
          Used for database storage, authentication, and real-time functionality. Data is encrypted and handled in accordance with Supabase's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.5 Twilio</Text>
        <Text style={styles.paragraph}>
          Used for SMS notifications and phone number verification. Phone numbers are shared to deliver verification codes and order-related messages. Governed by Twilio's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.6 PostHog</Text>
        <Text style={styles.paragraph}>
          Used for product analytics, user behavior analysis, and session replay to improve app usability. Data is governed by PostHog's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.7 Sentry</Text>
        <Text style={styles.paragraph}>
          Used for application error monitoring and crash reporting. Technical data may be collected to diagnose and resolve issues. Governed by Sentry's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.8 Expo Push Notification Service</Text>
        <Text style={styles.paragraph}>
          Used to deliver push notifications. Device tokens and delivery metadata are processed to enable notification delivery. Governed by Expo's Privacy Policy.
        </Text>

        <Text style={styles.subSection}>8.9 Google Sign-In / Apple Sign-In</Text>
        <Text style={styles.paragraph}>
          Used for optional authentication. When used, we receive your name, email address, and authentication credentials as permitted by the provider.
        </Text>

        <Text style={styles.section}>9. Contact Us</Text>
        <Text style={styles.paragraph}>
          If you have questions or concerns about this Privacy Policy or our data practices, please contact 
          us at:
        </Text>
        <Text style={styles.paragraph}>Email: support@jadetradeapp.com</Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  content: { padding: 16 },
  title: { fontSize: 24, fontWeight: "bold", marginBottom: 8, color: colors.text },
  meta: { fontSize: 12, color: colors.textLight, marginBottom: 4 },
  section: { fontSize: 18, fontWeight: "bold", marginTop: 16, marginBottom: 4, color: colors.text },
  subSection: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 12,
    marginBottom: 4,
    color: colors.text,
  },
  paragraph: { fontSize: 14, lineHeight: 20, marginBottom: 8, color: colors.text },
  list: { marginLeft: 16, marginBottom: 8 },
  listItem: { fontSize: 14, lineHeight: 20, marginBottom: 4, color: colors.text },
});
